import React, { useState, useEffect, useCallback } from 'react';
import { visaCriteriaAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import ConfirmModal from '../components/ConfirmModal';
import Toast from '../components/Toast';

/**
 * Visa Criteria — the admin-tunable substrate behind the visa scoring engine.
 * Pick a visa, then edit the criteria the LLM judge scores against (rubrics
 * for met/partial/unmet), each criterion's weight, the evidence (DocTypes)
 * the application flow asks for, and the per-visa eligibility gate (JSON).
 * Changes take effect in the user panel with no code deploy.
 */

const EMPTY = {
  criterion_code: '', criterion_name: '', description: '',
  rubric_met: '', rubric_partial: '', rubric_unmet: '',
  weight: 1.0, evidence_required: [], display_order: 0, is_active: 1,
};

const VisaCriteria = () => {
  const [visas, setVisas] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [selectedVisaId, setSelectedVisaId] = useState('');
  const [criteria, setCriteria] = useState([]);
  const [eligText, setEligText] = useState('{}');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {mode:'create'|'edit', id?}
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingElig, setSavingElig] = useState(false);
  const [pricing, setPricing] = useState({ processing_time: '', cost_display: '', total_cost: '', advance_cost: '', fee_breakdown: [] });
  const [savingPricing, setSavingPricing] = useState(false);

  // Load the visa picker once.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await visaCriteriaAPI.getVisas();
        setVisas(data.visas || []);
        setDocTypes(data.docTypes || []);
        if (data.visas && data.visas.length) setSelectedVisaId(String(data.visas[0].id));
      } catch (e) {
        setToast({ type: 'error', message: 'Failed to load visas' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fetchCriteria = useCallback(async () => {
    if (!selectedVisaId) return;
    try {
      setLoading(true);
      const { data } = await visaCriteriaAPI.getCriteria(selectedVisaId);
      setCriteria(data.criteria || []);
      setEligText(JSON.stringify(data.eligibilityRules || {}, null, 2));
      if (data.pricing) setPricing({ processing_time: '', cost_display: '', total_cost: '', advance_cost: '', fee_breakdown: [], ...data.pricing, fee_breakdown: data.pricing.fee_breakdown || [] });
      if (data.docTypes) setDocTypes(data.docTypes);
    } catch (e) {
      setToast({ type: 'error', message: 'Failed to load criteria' });
    } finally {
      setLoading(false);
    }
  }, [selectedVisaId]);

  useEffect(() => { fetchCriteria(); }, [fetchCriteria]);

  const openCreate = () => { setForm({ ...EMPTY, display_order: criteria.length }); setModal({ mode: 'create' }); };
  const openEdit = (c) => {
    setForm({
      criterion_code: c.criterion_code || '', criterion_name: c.criterion_name || '',
      description: c.description || '', rubric_met: c.rubric_met || '',
      rubric_partial: c.rubric_partial || '', rubric_unmet: c.rubric_unmet || '',
      weight: c.weight != null ? c.weight : 1.0,
      evidence_required: Array.isArray(c.evidence_required) ? c.evidence_required : [],
      display_order: c.display_order || 0, is_active: c.is_active,
    });
    setModal({ mode: 'edit', id: c.id });
  };

  const toggleEvidence = (code) => {
    setForm((f) => ({
      ...f,
      evidence_required: f.evidence_required.includes(code)
        ? f.evidence_required.filter((x) => x !== code)
        : [...f.evidence_required, code],
    }));
  };

  const save = async () => {
    if (!form.criterion_code.trim() || !form.criterion_name.trim()) {
      setToast({ type: 'error', message: 'Criterion code and name are required' });
      return;
    }
    try {
      setSaving(true);
      if (modal.mode === 'create') {
        await visaCriteriaAPI.create({ ...form, visa_config_id: Number(selectedVisaId) });
        setToast({ type: 'success', message: 'Criterion created' });
      } else {
        await visaCriteriaAPI.update(modal.id, form);
        setToast({ type: 'success', message: 'Criterion updated' });
      }
      setModal(null);
      fetchCriteria();
    } catch (e) {
      setToast({ type: 'error', message: e?.response?.data?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await visaCriteriaAPI.delete(deleteId);
      setToast({ type: 'success', message: 'Criterion deleted' });
      setDeleteId(null);
      fetchCriteria();
    } catch (e) {
      setToast({ type: 'error', message: 'Delete failed' });
    }
  };

  const saveEligibility = async () => {
    let parsed;
    try { parsed = JSON.parse(eligText); } catch {
      setToast({ type: 'error', message: 'Eligibility rules must be valid JSON' });
      return;
    }
    try {
      setSavingElig(true);
      await visaCriteriaAPI.updateEligibility(selectedVisaId, parsed);
      setToast({ type: 'success', message: 'Eligibility rules saved' });
    } catch (e) {
      setToast({ type: 'error', message: e?.response?.data?.message || 'Save failed' });
    } finally {
      setSavingElig(false);
    }
  };

  const savePricing = async () => {
    try {
      setSavingPricing(true);
      await visaCriteriaAPI.updatePricing(selectedVisaId, pricing);
      setToast({ type: 'success', message: 'Pricing & timing saved' });
    } catch (e) {
      setToast({ type: 'error', message: e?.response?.data?.message || 'Save failed' });
    } finally {
      setSavingPricing(false);
    }
  };
  const setFee = (i, idx, val) => setPricing((p) => { const f = p.fee_breakdown.map((r) => [...r]); f[i][idx] = idx === 1 ? val : val; return { ...p, fee_breakdown: f }; });
  const addFee = () => setPricing((p) => ({ ...p, fee_breakdown: [...p.fee_breakdown, ['', '']] }));
  const rmFee = (i) => setPricing((p) => ({ ...p, fee_breakdown: p.fee_breakdown.filter((_, x) => x !== i) }));

  const inputStyle = { width: '100%', padding: '9px 11px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6, display: 'block' };
  const STATUS_COLOR = { met: '#16A34A', partial: '#D97706', unmet: '#DC2626' };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">VISA CRITERIA &amp; SCORING</h1>
            <p className="page-subtitle">RUBRICS, WEIGHTS, EVIDENCE &amp; ELIGIBILITY — TUNES THE SCORING ENGINE</p>
          </div>
          <div className="header-right" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select value={selectedVisaId} onChange={(e) => setSelectedVisaId(e.target.value)} style={{ ...inputStyle, width: 'auto', maxWidth: 320 }}>
              {visas.map((v) => <option key={v.id} value={v.id}>{v.visa_name} ({v.destination_code})</option>)}
            </select>
            <button className="btn-primary" onClick={openCreate} disabled={!selectedVisaId}>+ Add Criterion</button>
          </div>
        </div>

        <div style={{ padding: '0 24px 40px' }}>
          {loading ? (
            <p style={{ color: '#6B7280' }}>Loading…</p>
          ) : (
            <>
              {criteria.length === 0 ? (
                <p style={{ color: '#6B7280' }}>No criteria for this visa yet. Add the first one.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {criteria.map((c) => (
                    <div key={c.id} style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, background: '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#9CA3AF' }}>{c.criterion_code}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#013399', padding: '2px 9px', borderRadius: 999 }}>weight {Number(c.weight).toFixed(1)}</span>
                        {!c.is_active && <span style={{ fontSize: 11, color: '#DC2626' }}>(inactive)</span>}
                        <div style={{ flex: 1 }} />
                        <button onClick={() => openEdit(c)} style={{ border: '1px solid #E5E7EB', background: '#F9FAFB', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13 }}>Edit</button>
                        <button onClick={() => setDeleteId(c.id)} style={{ border: '1px solid #FEE2E2', background: '#FEF2F2', color: '#DC2626', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13 }}>Delete</button>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 4 }}>{c.criterion_name}</div>
                      {c.description && <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>{c.description}</div>}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                        {['met', 'partial', 'unmet'].map((k) => (
                          <div key={k} style={{ fontSize: 12, color: '#4B5563', background: '#F9FAFB', borderRadius: 8, padding: '8px 10px' }}>
                            <div style={{ fontWeight: 700, color: STATUS_COLOR[k], textTransform: 'uppercase', fontSize: 10, marginBottom: 3 }}>{k}</div>
                            {c[`rubric_${k}`] || <span style={{ color: '#D1D5DB' }}>—</span>}
                          </div>
                        ))}
                      </div>
                      {Array.isArray(c.evidence_required) && c.evidence_required.length > 0 && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#9CA3AF' }}>evidence: {c.evidence_required.join(', ')}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Eligibility gate editor */}
              <div style={{ marginTop: 28, border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, background: '#fff' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Eligibility gate (JSON)</div>
                <div style={{ fontSize: 12.5, color: '#6B7280', marginBottom: 10 }}>
                  Profile-completeness gate for this visa. Shape: {'{ "required": ["jobTitle","company"], "optional": ["awards"], "minOptionalCount": 1, "minYearsExperience": 3 }'}
                </div>
                <textarea value={eligText} onChange={(e) => setEligText(e.target.value)} rows={7} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 13, resize: 'vertical' }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                  <button className="btn-primary" onClick={saveEligibility} disabled={savingElig}>{savingElig ? 'Saving…' : 'Save eligibility'}</button>
                </div>
              </div>

              {/* Pricing & timing editor — real data shown on the catalog/application */}
              <div style={{ marginTop: 20, border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, background: '#fff' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Pricing &amp; timing</div>
                <div style={{ fontSize: 12.5, color: '#6B7280', marginBottom: 14 }}>
                  Shown on the visa card &amp; application. Leave blank to omit (the application falls back to a labelled estimate).
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div><label style={labelStyle}>Processing time</label><input value={pricing.processing_time} onChange={(e) => setPricing({ ...pricing, processing_time: e.target.value })} placeholder="2–4 months" style={inputStyle} /></div>
                  <div><label style={labelStyle}>Headline cost</label><input value={pricing.cost_display} onChange={(e) => setPricing({ ...pricing, cost_display: e.target.value })} placeholder="from $6,000" style={inputStyle} /></div>
                  <div><label style={labelStyle}>Total cost (number)</label><input type="number" value={pricing.total_cost} onChange={(e) => setPricing({ ...pricing, total_cost: e.target.value })} placeholder="6000" style={inputStyle} /></div>
                  <div><label style={labelStyle}>Advance (number)</label><input type="number" value={pricing.advance_cost} onChange={(e) => setPricing({ ...pricing, advance_cost: e.target.value })} placeholder="1000" style={inputStyle} /></div>
                </div>
                <label style={labelStyle}>Fee breakdown (label + amount)</label>
                {pricing.fee_breakdown.map((row, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input value={row[0]} onChange={(e) => setFee(i, 0, e.target.value)} placeholder="Naytive case build" style={{ ...inputStyle, flex: 2 }} />
                    <input type="number" value={row[1]} onChange={(e) => setFee(i, 1, e.target.value)} placeholder="3250" style={{ ...inputStyle, flex: 1 }} />
                    <button onClick={() => rmFee(i)} style={{ border: '1px solid #FEE2E2', background: '#FEF2F2', color: '#DC2626', borderRadius: 6, padding: '0 12px', cursor: 'pointer' }}>×</button>
                  </div>
                ))}
                <button onClick={addFee} style={{ border: '1px solid #E5E7EB', background: '#F9FAFB', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>+ Add fee line</button>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button className="btn-primary" onClick={savePricing} disabled={savingPricing}>{savingPricing ? 'Saving…' : 'Save pricing'}</button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setModal(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: 'min(720px, 100%)', maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 18px' }}>{modal.mode === 'create' ? 'Add Criterion' : 'Edit Criterion'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Criterion code</label>
                <input value={form.criterion_code} onChange={(e) => setForm({ ...form, criterion_code: e.target.value })} placeholder="e.g. AWARDS" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Criterion name</label>
                <input value={form.criterion_name} onChange={(e) => setForm({ ...form, criterion_name: e.target.value })} placeholder="Awards & recognition" style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Description</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Rubric — MET (what fully satisfies this)</label>
              <textarea value={form.rubric_met} onChange={(e) => setForm({ ...form, rubric_met: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Rubric — PARTIAL</label>
              <textarea value={form.rubric_partial} onChange={(e) => setForm({ ...form, rubric_partial: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Rubric — UNMET</label>
              <textarea value={form.rubric_unmet} onChange={(e) => setForm({ ...form, rubric_unmet: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Evidence required (documents the application asks for)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {docTypes.map((code) => (
                  <label key={code} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 999, padding: '4px 12px', cursor: 'pointer', background: form.evidence_required.includes(code) ? '#EAF1FF' : '#fff' }}>
                    <input type="checkbox" checked={form.evidence_required.includes(code)} onChange={() => toggleEvidence(code)} />
                    {code}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 14, marginBottom: 20, alignItems: 'end' }}>
              <div>
                <label style={labelStyle}>Weight</label>
                <input type="number" step="0.1" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Display order</label>
                <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} style={inputStyle} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, paddingBottom: 8 }}>
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} /> Active
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setModal(null)} style={{ border: '1px solid #E5E7EB', background: '#fff', borderRadius: 8, padding: '9px 18px', cursor: 'pointer' }}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <ConfirmModal
          title="Delete criterion?"
          message="This removes the criterion from the scoring rubric. This cannot be undone."
          onConfirm={doDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </div>
  );
};

export default VisaCriteria;
